import { bareSubdivisionCode } from '@vacationtracker/shared/data/countries'
import { holidayDay } from '@vacationtracker/shared/functions/holiday-tags'
import { IGetHolidaysResponse } from '@vacationtracker/shared/types/holidays'

export interface IExistingHoliday {
  id: string
  name: string
  date: string
  type?: string | null
  thirdPartyName?: string | null
  multiDayId?: string | null
  recurring?: boolean | null
  isHalfDay?: boolean | null
}

export interface IImportPlace {
  country?: string | null
  state?: string | null
}

export interface IReplacedImportsInput {
  existing: IExistingHoliday[]
  previous?: IImportPlace | null
  picked: IImportPlace
  /** The new place's library holidays for the page year. */
  library?: IGetHolidaysResponse[]
}

export interface ILostImportsInput extends IReplacedImportsInput {
  /** Every row in the years the place change clears. */
  affected: IExistingHoliday[]
}

export interface IExistingHolidaysByYear {
  year: string
  holidays: IExistingHoliday[]
}

export interface IDaySpan {
  start: string
  end: string
}

export const rowId = (_row: IGetHolidaysResponse, index: number): string => String(index)

export const holidaysOfYear = (byYear: IExistingHolidaysByYear[], year: number): IExistingHoliday[] =>
  byYear.filter((entry) => Number(entry.year) === year).flatMap((entry) => entry.holidays)

const isPlainImport = (row: IExistingHoliday): boolean => row.type === 'LIB' && row.name === row.thirdPartyName

/** The backend refreshes an unrenamed import of the same library holiday on the same day in place. */
const isSameImport = (row: IExistingHoliday, incoming: IGetHolidaysResponse): boolean => isPlainImport(row) && row.thirdPartyName === incoming.name

const isTwin = (row: IExistingHoliday, library: IGetHolidaysResponse[]): boolean =>
  library.some((incoming) => holidayDay(row.date) === holidayDay(incoming.valueForApi) && isSameImport(row, incoming))

// Old import events stored lowercase states ('on'), so places compare upper-cased, as the backend does.
const placeKey = ({ country, state }: IImportPlace): string => `${(country || '').toUpperCase()}|${(bareSubdivisionCode(state || null) || '').toUpperCase()}`

export const samePlace = (a: IImportPlace, b: IImportPlace): boolean => placeKey(a) === placeKey(b)

/** No earlier import counts as the same place. */
export const placeChanged = (previous: IImportPlace | null | undefined, picked: IImportPlace): boolean => (previous ? !samePlace(previous, picked) : false)

/**
 * The unrenamed imports the backend removes when the location's previous import was for another place.
 * The backend pairs twins before that strip, so a twin of an incoming holiday is refreshed in place instead.
 */
export const replacedImports = ({ existing, previous, picked, library = [] }: IReplacedImportsInput): IExistingHoliday[] =>
  placeChanged(previous, picked) ? existing.filter((row) => isPlainImport(row) && !isTwin(row, library)) : []

/** What is left to clash with, or to refresh in place, once the replaced imports are gone. */
export const remainingAfterImport = (input: IReplacedImportsInput): IExistingHoliday[] => {
  const replaced = new Set(replacedImports(input))
  return input.existing.filter((row) => !replaced.has(row))
}

/** Distinct library holidays a place change removes for good: a name the new import refreshes in the page year comes back. */
export const lostImportCount = ({ affected, ...input }: ILostImportsInput): number => {
  const refreshed = new Set(input.existing.filter((row) => isPlainImport(row) && isTwin(row, input.library ?? [])).map((row) => row.thirdPartyName))
  const lost = replacedImports({ ...input, existing: affected })
    .map((row) => row.thirdPartyName)
    .filter((name) => !refreshed.has(name))
  return new Set(lost).size
}

/** Rows are stored one per day, so a multi-day holiday clashes on any of its days. */
export const clashingRow = (existing: IExistingHoliday[], incoming: IGetHolidaysResponse): IExistingHoliday | undefined =>
  existing.find((row) => holidayDay(row.date) === holidayDay(incoming.valueForApi) && !isSameImport(row, incoming))

/** The same library holiday already imported on that day, which the import refreshes in place rather than adding again. */
export const alreadyImported = (existing: IExistingHoliday[], incoming: IGetHolidaysResponse): boolean =>
  existing.some((row) => holidayDay(row.date) === holidayDay(incoming.valueForApi) && isSameImport(row, incoming))

export const isRepeatingCustom = (row: IExistingHoliday): boolean => row.type === 'CUSTOM' && row.recurring === true

/**
 * Replace removes a repeating custom holiday in the page year only. Earlier than next year, next year's copy still
 * repeats and the yearly import carries it on; in next year, the last year written, nothing is left to carry it.
 */
export const endsSeries = (row: IExistingHoliday, year: number, today: number): boolean => isRepeatingCustom(row) && year > today

export const daySpan = (existing: IExistingHoliday[], row: IExistingHoliday): IDaySpan | undefined => {
  if (!row.multiDayId) return undefined
  const days = [...new Set(existing.filter((other) => other.multiDayId === row.multiDayId).map((other) => holidayDay(other.date)))].sort()
  return days.length > 1 ? { start: days[0], end: days[days.length - 1] } : undefined
}

/** Public holidays start selected unless something else is already on that day. */
export const initialSelection = (library: IGetHolidaysResponse[], existing: IExistingHoliday[]): Set<string> =>
  new Set(library.flatMap((holiday, index) => (holiday.type === 'public' && !clashingRow(existing, holiday) ? [rowId(holiday, index)] : [])))
